import type {
  ClientTasksUpdatedData,
  ClientTasksUpdatedNotification,
  ClientTasksUpdatedNotificationData,
} from 'app/modules/RealtimeNotificationsProvider/types/clientTasksUpdated';
import { NOTIFICATION_TYPES } from 'app/modules/RealtimeNotificationsProvider/types/notificationTypes';
import type { NextTaskItem } from './api';

function normalizeEvent(value: string) {
  return value.trim().toLowerCase();
}

function unwrapClientTasksUpdatedData(
  raw: ClientTasksUpdatedNotificationData | undefined,
): ClientTasksUpdatedData | undefined {
  if (!raw) return undefined;
  const nested = 'data' in raw ? raw.data : undefined;
  if (nested?.event) return nested;
  if ('event' in raw && typeof (raw as { event?: unknown }).event === 'string') {
    return raw as ClientTasksUpdatedData;
  }
  return undefined;
}

function applyToMatchingTask(
  tasks: NextTaskItem[],
  eventKey: string,
  update: (task: NextTaskItem) => NextTaskItem,
): NextTaskItem[] | undefined {
  let changed = false;
  const next = tasks.map((task) => {
    const taskEvent = task.event ? normalizeEvent(task.event) : '';
    if (!taskEvent || taskEvent !== eventKey) return task;
    changed = true;
    return update(task);
  });
  return changed ? next : undefined;
}

export function applyClientTasksRealtimePatch(
  tasks: NextTaskItem[] | undefined,
  notification: Pick<ClientTasksUpdatedNotification, 'type' | 'data'>,
): NextTaskItem[] | undefined {
  if (!tasks?.length || notification.type !== NOTIFICATION_TYPES.CLIENT_TASKS_UPDATED) {
    return tasks;
  }

  const data = unwrapClientTasksUpdatedData(notification.data);
  if (!data?.event) return tasks;

  const eventKey = normalizeEvent(String(data.event));
  const eventType = data.eventType;

  switch (eventType) {
    case 'increment':
    case 'decrement': {
      const delta = typeof data.delta === 'number' ? data.delta : 1;
      return (
        applyToMatchingTask(tasks, eventKey, (task) => {
          const base = task.value ?? 0;
          const newVal =
            typeof data.value === 'number'
              ? data.value
              : eventType === 'increment'
                ? base + delta
                : base - delta;
          return { ...task, value: Math.max(0, newVal) };
        }) ?? tasks
      );
    }
    case 'complete':
      return applyToMatchingTask(tasks, eventKey, (task) => ({ ...task, finished: true })) ?? tasks;
    case 'hide': {
      const hidden = typeof data.value === 'boolean' ? data.value : true;
      return applyToMatchingTask(tasks, eventKey, (task) => ({ ...task, hidden })) ?? tasks;
    }
    default:
      return tasks;
  }
}
